import { useMemo } from 'react';
import { useAppSelector } from '@/core/global-store';

export type SubscriptionPlanPayload = {
    id?: number;
    slug?: string;
    name?: string;
    name_en?: string;
    limits?: Record<string, number>;
    features?: Record<string, boolean>;
    price?: number;
    billing_period?: string;
    trial_days?: number;
    is_featured?: boolean;
};

export type SubscriptionPayload = {
    id?: number;
    status?: string;
    trial_ends_at?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
    days_remaining?: number | null;
    is_active?: boolean;
    plan?: SubscriptionPlanPayload;
    usage?: Record<string, number>;
};

export function useSubscription(): SubscriptionPayload | null {
    const me = useAppSelector((s) => s.auth.me);

    return useMemo(() => {
        const sub = (me as Record<string, unknown> | null)?.subscription;
        if (!sub || typeof sub !== 'object') {
            return null;
        }
        return sub as SubscriptionPayload;
    }, [me]);
}

export function usePlanFeature(feature: string): boolean {
    const subscription = useSubscription();

    return useMemo(() => {
        if (!subscription?.is_active && subscription?.status !== 'trial') {
            return false;
        }
        return Boolean(subscription?.plan?.features?.[feature]);
    }, [subscription, feature]);
}

export function usePlanLimit(limitKey: string): { current: number; max: number | null; atLimit: boolean } {
    const subscription = useSubscription();

    return useMemo(() => {
        const usageKey = limitKey.replace('max_', '');
        const current = subscription?.usage?.[usageKey] ?? 0;
        const max = subscription?.plan?.limits?.[limitKey] ?? null;
        const atLimit = max !== null && max >= 0 && current >= max;
        return { current, max, atLimit };
    }, [subscription, limitKey]);
}
